// RFC 9457 problem+json: the only error shape the API returns (spec/08-api-design.md §2.6).
//
// The catalogue of codes is in `src/errors/catalogue.ts`; this file owns the envelope they travel
// in. Every failing response — validation, conflict, rate limit, 500 — is one of these.
import { z } from "../zod.js";
import { RequestIdSchema } from "../ids/schema.js";
import { MoneySchema } from "./money.js";
import { ESSENTIAL_VENUE_SETUP_STEPS } from "./setup-steps.js";
/** `application/problem+json`, per RFC 9457. */
export const PROBLEM_CONTENT_TYPE = 'application/problem+json';
/** One field-level validation problem inside `errors[]`. */
export const ProblemFieldErrorSchema = z
    .object({
    /** The field that failed, as a dotted path into the request body: `lines.0.quantity`. */
    field: z.string().min(1),
    /** A short machine-readable reason: `min`, `required`, `pattern`. */
    code: z.string().min(1),
    message: z.string().min(1),
})
    .openapi('ProblemFieldError', { description: 'A field-level validation problem.' });
/** Where a player's running session is (the `already-in-session` problem's `activeSession`, F-SES-01). */
export const ProblemActiveSessionSchema = z
    .object({
    /** The station's name, or "PC 07" from its number. */
    station: z.string().min(1).max(120),
    /** The venue's name. */
    venue: z.string().min(1).max(200),
})
    .openapi('ProblemActiveSession', { description: 'Where the player’s running session is (F-SES-01).' });
export const ProblemSchema = z
    .object({
    /** `https://errors.{domain}/{slug}` — the slug is kebab-case and identifies the failure. */
    type: z.url(),
    /** A short, human-readable summary. Stable for a given `type`; never localised per request. */
    title: z.string().min(1),
    status: z.int().min(400).max(599),
    /** What went wrong *this* time. Never reveals whether an account exists (§2.6). */
    detail: z.string().optional(),
    /** The catalogue code, snake_case. One code per failure; clients branch on this, not on `title`. */
    code: z.string().regex(/^[a-z][a-z0-9]*(?:_[a-z0-9]+)*$/, 'Expected a snake_case error code'),
    /** Also returned as the `X-Request-Id` header and used as the trace id (§2.11). */
    requestId: RequestIdSchema,
    /** Field-level problems, for `400` validation failures. */
    errors: z.array(ProblemFieldErrorSchema).optional(),
    /** RFC 9457's optional pointer to the specific occurrence. */
    instance: z.string().optional(),
    /**
     * An RFC 9457 extension member, only on `already-in-session` (spec/05 §3.2 check 6, F-SES-01): where the
     * player's running session is — the station's name ("PC 07") and the venue's — so the kiosk can say
     * "You are signed in on PC 07 at Koramangala" and offer **Move here**. Absent on every other problem.
     */
    activeSession: ProblemActiveSessionSchema.optional(),
    /**
     * An RFC 9457 extension member, only on `price-changed` (spec/08 §2.9, F-SES-17 rule 3): the total the server
     * computes now, so the client can show it and ask the person to confirm. Absent on every other problem.
     */
    total: MoneySchema.optional(),
    /**
     * An RFC 9457 extension member, only on `venue-not-ready` (ADR-0047, F-HQ-03): the venue's essential setup steps
     * still missing, in order — `openingHours`, `price`, `stationPaired` — so the console can name them beside Go
     * live. Absent on every other problem.
     */
    missingSteps: z.array(z.enum(ESSENTIAL_VENUE_SETUP_STEPS)).optional(),
})
    .openapi('Problem', {
    description: 'An RFC 9457 problem document. Every error response in the API has this shape.',
});
//# sourceMappingURL=problem.js.map